import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';
import { useAuthStore } from '@/features/auth';
import { UserAvatar } from '@/components/ui';

/** Height of the control bar (48px) + top border accent (4px) */
const SESSION_HEIGHT = 52;

/**
 * ViewAsOverlay — immersive "Electric Blue" remote session UI.
 *
 * Sets `--view-as-offset` on `:root` so every fixed/sticky element in the
 * app shifts down automatically (sidebar, admin header, bottom nav, etc.).
 *
 * Three visual layers:
 *  1. Breathing electric-blue border frame with glow
 *  2. Content vignette — electric inner shadow
 *  3. Dark "Command Center" control bar with deep shadow
 */
export function ViewAsOverlay() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, isImpersonating, stopImpersonation } = useAuthStore();
  const containerRef = useRef<HTMLDivElement>(null);

  // Keep --view-as-offset in sync (instant, no GSAP needed for the variable)
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--view-as-offset',
      isImpersonating ? `${SESSION_HEIGHT}px` : '0px',
    );
    return () => {
      document.documentElement.style.setProperty('--view-as-offset', '0px');
    };
  }, [isImpersonating]);

  useGSAP(
    () => {
      if (!isImpersonating) return;

      // Synchronized entrance timeline
      const tl = gsap.timeline({ defaults: { ease: 'power4.out', duration: 0.7 } });

      // 1. Slide bar in from above
      tl.fromTo(
        '.vas-bar',
        { y: -SESSION_HEIGHT, opacity: 0 },
        { y: 0, opacity: 1 },
        0,
      );

      // 2. Fade in border frame
      tl.fromTo('.vas-frame', { opacity: 0 }, { opacity: 1, duration: 0.5 }, 0.1);

      // 3. Breathing electric border — infinite glow pulse (after entrance)
      gsap.to('.vas-frame', {
        borderColor: 'rgba(0, 240, 255, 1)',
        boxShadow:
          '0 0 25px 5px rgba(0, 240, 255, 0.6), inset 0 0 25px 5px rgba(0, 240, 255, 0.15)',
        duration: 2,
        repeat: -1,
        yoyo: true,
        ease: 'power1.inOut',
        delay: 0.7,
      });
    },
    { scope: containerRef, dependencies: [isImpersonating] },
  );

  if (!isImpersonating || !user) return null;

  const handleStop = () => {
    stopImpersonation();
    queryClient.invalidateQueries({ queryKey: ['auth', 'profile'] });
    navigate('/admin/user-impersonation');
  };

  const fullName = `${user.firstName} ${user.lastName}`;
  const roleObj = user.employmentRole;
  const roleName = roleObj
    ? (i18n.language === 'en' && roleObj.nameEn ? roleObj.nameEn : roleObj.name)
    : null;

  return (
    <div ref={containerRef}>
      {/* Layer 1 — Breathing Electric Border Frame */}
      <div
        className="vas-frame fixed inset-0 z-[9998] pointer-events-none"
        style={{
          border: '5px solid rgba(0, 240, 255, 0.5)',
          borderRadius: '4px',
          boxShadow:
            '0 0 15px 2px rgba(0, 240, 255, 0.3), inset 0 0 20px 5px rgba(0, 240, 255, 0.15)',
          opacity: 0,
        }}
      />

      {/* Layer 2 — Electric Vignette */}
      <div
        className="vas-vignette fixed inset-0 z-[9997] pointer-events-none"
        style={{
          boxShadow: 'inset 0 0 120px rgba(0, 240, 255, 0.04)',
        }}
      />

      {/* Layer 3 — Command Center Control Bar */}
      <div
        className="vas-bar fixed top-0 left-0 w-full z-[9999]"
        dir="ltr"
        style={{
          height: `${SESSION_HEIGHT}px`,
          background: 'var(--color-remote-dark)',
          borderBottom: '1px solid rgba(0, 240, 255, 0.2)',
          boxShadow: '0 10px 40px -10px rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
        }}
      >
        {/* Electric-blue top accent line */}
        <div
          className="absolute top-0 left-0 w-full"
          style={{
            height: '3px',
            background: 'linear-gradient(90deg, transparent, var(--color-electric-blue), transparent)',
            opacity: 0.6,
          }}
        />

        <div className="relative h-full flex items-center justify-center px-3">
          {/* Left — "Remote Session" pill (desktop only) */}
          <div className="absolute left-4 hidden md:flex items-center">
            <div
              className="flex items-center gap-2 px-3 py-1 rounded-full"
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
              </span>
              <span
                className="text-[10px] font-bold uppercase tracking-widest"
                style={{ color: 'rgba(255, 255, 255, 0.7)' }}
              >
                {t('admin.impersonation.remoteSession')}
              </span>
            </div>
          </div>

          {/* Mobile: live dot only (visible below md) */}
          <span className="absolute left-3 flex h-2 w-2 flex-shrink-0 md:hidden">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
          </span>

          {/* Center — avatar + name + role + End Session */}
          <div className="flex items-center gap-2 sm:gap-3">
            <UserAvatar
              src={user.avatarUrl}
              name={fullName}
              size="xs"
              className="ring-2 ring-[var(--color-electric-blue)] flex-shrink-0"
            />
            <span className="text-xs sm:text-sm font-medium text-white/90 whitespace-nowrap">
              <span className="text-white/50 hidden sm:inline">Viewing as </span>
              <span style={{ color: 'var(--color-electric-blue)' }}>{fullName}</span>
            </span>

            {roleName && (
              <span
                className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full flex-shrink-0 hidden sm:inline-block"
                style={{
                  color: 'rgba(255, 255, 255, 0.5)',
                  background: 'rgba(255, 255, 255, 0.06)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                }}
              >
                {roleName}
              </span>
            )}

            {/* Divider */}
            <div
              className="h-4 w-px flex-shrink-0"
              style={{ background: 'rgba(255, 255, 255, 0.15)' }}
            />

            {/* End Session button */}
            <button
              onClick={handleStop}
              className="group relative px-3 sm:px-5 py-1.5 overflow-hidden rounded text-white text-[10px] sm:text-xs font-bold uppercase tracking-wider transition-all hover:scale-105 active:scale-95 cursor-pointer"
              style={{ background: 'var(--color-session-danger)' }}
            >
              <span className="relative z-10">
                {t('admin.impersonation.endSession')}
              </span>
              <div
                className="absolute inset-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300"
                style={{ background: 'rgba(255, 255, 255, 0.2)' }}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Backwards-compatible alias */
export const ImpersonationBadge = ViewAsOverlay;

export default ViewAsOverlay;
